import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@services/api';
import type { ARInvoice } from '../types/index';
import { DataTable } from '@components/DataTable';
import { StatusBadge } from '@components/StatusBadge';
import { ARInvoiceForm } from '@components/ARInvoiceForm';
import { Search, Download, RefreshCw, Eye, Plus, Home } from 'lucide-react';
import { format } from 'date-fns';

export const ARInvoices: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<ARInvoice | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Fetch AR invoices from live database
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ar-invoices', page, pageSize],
    queryFn: async () => {
      try {
        const response = await apiClient.getARInvoices(page, pageSize);
        return response;
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message || 'Failed to fetch AR invoices'
        );
      }
    },
    staleTime: 30000, // 30 seconds
    retry: 3,
  });

  // Filter invoices based on search and status
  const filteredInvoices = React.useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((invoice: ARInvoice) => {
      const matchesSearch =
        invoice.invoiceNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.customerName
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const handleExport = async () => {
    try {
      const csvData = await apiClient.exportLogs('csv');
      const url = window.URL.createObjectURL(new Blob([csvData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ar-invoices-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleCreateInvoice = async (invoiceData: Partial<ARInvoice>) => {
    try {
      setFormLoading(true);
      // Call API to create invoice
      // await apiClient.createARInvoice(invoiceData);
      console.log('Creating invoice:', invoiceData);
      // Refetch data
      await refetch();
      setShowForm(false);
    } catch (error) {
      console.error('Failed to create invoice:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const columns = [
    {
      key: 'invoiceNumber' as const,
      label: 'Invoice Number',
      width: '150px',
    },
    {
      key: 'invoiceDate' as const,
      label: 'Date',
      width: '120px',
      render: (value: string) => format(new Date(value), 'MMM dd, yyyy'),
    },
    {
      key: 'customerName' as const,
      label: 'Customer',
      width: '200px',
    },
    {
      key: 'amount' as const,
      label: 'Amount',
      width: '120px',
      render: (value: number, row: ARInvoice) =>
        `${row.currency} ${value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: 'status' as const,
      label: 'Status',
      width: '150px',
      render: (value: string) => <StatusBadge status={value || 'pending'} />,
    },
    {
      key: 'irn' as const,
      label: 'IRN',
      width: '150px',
      render: (value: string) => value || '-',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AR Invoices</h1>
              <p className="text-gray-600 mt-1">
                Manage and track outgoing invoices
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice or customer..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="error">Error</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>

            {/* Create Button */}
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
              Create Invoice
            </button>
          </div>

          {/* Results Info */}
          <div className="text-sm text-gray-600">
            {isLoading ? (
              'Loading...'
            ) : error ? (
              <span className="text-red-600">Error: {error.message}</span>
            ) : (
              <>
                Showing {filteredInvoices.length} of{' '}
                {data?.pagination?.total || 0} invoices
              </>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredInvoices}
            loading={isLoading}
            error={error?.message}
            rowKey="id"
            onRowClick={(invoice) => setSelectedInvoice(invoice)}
            pagination={{
              page,
              pageSize,
              total: data?.pagination?.total || 0,
              onPageChange: setPage,
            }}
          />
        </div>

        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Invoice Details
                </h2>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Invoice Number</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedInvoice.invoiceNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <div className="mt-1">
                      <StatusBadge status={selectedInvoice.status as any} />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invoice Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {format(new Date(selectedInvoice.invoiceDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Due Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {format(new Date(selectedInvoice.dueDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedInvoice.customerName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedInvoice.currency}{' '}
                      {selectedInvoice.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>

                {/* IRN and Hash */}
                {selectedInvoice.irn && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600">IRN</p>
                    <p className="text-sm font-mono text-gray-900 break-all">
                      {selectedInvoice.irn}
                    </p>
                  </div>
                )}

                {selectedInvoice.hash && (
                  <div>
                    <p className="text-sm text-gray-600">Hash</p>
                    <p className="text-sm font-mono text-gray-900 break-all">
                      {selectedInvoice.hash}
                    </p>
                  </div>
                )}

                {/* Line Items */}
                {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      Line Items
                    </p>
                    <div className="space-y-2">
                      {selectedInvoice.lineItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="text-sm bg-gray-50 p-2 rounded"
                        >
                          <p className="font-medium text-gray-900">
                            {item.description}
                          </p>
                          <p className="text-gray-600">
                            Qty: {item.quantity} × {selectedInvoice.currency}{' '}
                            {item.unitPrice.toFixed(2)} = {selectedInvoice.currency}{' '}
                            {item.lineAmount.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="border-t border-gray-200 pt-4 flex gap-2">
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition">
                    <Eye className="w-4 h-4 inline mr-2" />
                    View Full Details
                  </button>
                  <button className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition">
                    Process Invoice
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Form Modal */}
      {showForm && (
        <ARInvoiceForm
          onSubmit={handleCreateInvoice}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}
    </div>
  );
};
